"use client"

import { useState, useEffect, Suspense } from "react"
import { useSession } from "next-auth/react"
import { useRouter, useSearchParams } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Header } from "@/components/header"
import { Check, Crown, Zap, Tag, X, Sparkles } from "lucide-react"
import Link from "next/link"
import { isFreeMode } from "@/lib/feature-flags"

interface SessionWithUser {
  user: {
    id: string
    email?: string | null
    name?: string | null
    image?: string | null
  }
}

type PlanType = 'monthly' | 'yearly'

function SubscriptionContent() {
  const { data: session } = useSession()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('yearly')
  const [userSubscription, setUserSubscription] = useState<{
    status: string
    planType?: string
    currentPeriodEnd?: string
    cancelAtPeriodEnd?: boolean
  } | null>(null)
  const [loadingSubscription, setLoadingSubscription] = useState(true)
  const [promoCode, setPromoCode] = useState("")
  const [appliedPromo, setAppliedPromo] = useState<{
    code: string
    discountPercentage: number
    description: string
  } | null>(null)

  const [evergreenPromo, setEvergreenPromo] = useState<{
    code: string
    discountPercentage: number
    description: string
  } | null>(null)
  const [promoError, setPromoError] = useState("")
  const [isValidatingPromo, setIsValidatingPromo] = useState(false)

  const success = searchParams.get("success")
  const canceled = searchParams.get("canceled")

  // Pricing
  const MONTHLY_PRICE = 4.99
  const YEARLY_PRICE = 49.99
  const YEARLY_MONTHLY_EQUIVALENT = (YEARLY_PRICE / 12).toFixed(2)
  const YEARLY_SAVINGS = ((MONTHLY_PRICE * 12 - YEARLY_PRICE) / (MONTHLY_PRICE * 12) * 100).toFixed(0)

  // Fetch and apply evergreen promo code by default
  useEffect(() => {
    if (isFreeMode) return
    let mounted = true;
    
    const fetchEvergreenPromo = async () => {
      try {
        const response = await fetch("/api/promo-codes/evergreen")
        if (response.ok && mounted) {
          const data = await response.json()
          setEvergreenPromo(data)
          setAppliedPromo(data)
        }
      } catch (error) {
        console.error("Error fetching evergreen promo:", error)
      }
    }

    fetchEvergreenPromo()
    
    return () => {
      mounted = false;
    }
  }, [])

  // Fetch user subscription status (only for logged in users)
  useEffect(() => {
    if (isFreeMode) {
      setLoadingSubscription(false)
      return
    }
    const fetchSubscription = async () => {
      if (!(session?.user as SessionWithUser['user'])?.id) {
        setLoadingSubscription(false)
        return
      }

      try {
        const accountResponse = await fetch("/api/account")
        if (accountResponse.ok) {
          const data = await accountResponse.json()
          setUserSubscription(data.subscription)
        }
      } catch (error) {
        console.error("Error fetching subscription:", error)
      } finally {
        setLoadingSubscription(false)
      }
    }

    fetchSubscription()
  }, [session])

  const handlePromoCodeValidation = async () => {
    if (!promoCode.trim()) {
      setPromoError("Please enter a promo code")
      return
    }

    if (!session?.user) {
      setPromoError("Please sign in to apply promo codes")
      return
    }

    setIsValidatingPromo(true)
    setPromoError("")

    try {
      const response = await fetch("/api/promo-codes/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: promoCode.trim() }),
      })

      const data = await response.json()

      if (response.ok && data.valid) {
        setAppliedPromo({
          code: data.code,
          discountPercentage: data.discountPercentage,
          description: data.description
        })
        setPromoCode("")
      } else if (response.status === 401) {
        setPromoError("Please sign in to apply promo codes")
      } else {
        setPromoError(data.error || "Invalid promo code")
      }
    } catch (error) {
      console.error("Promo code validation error:", error)
      setPromoError("Failed to validate promo code")
    } finally {
      setIsValidatingPromo(false)
    }
  }

  const removePromoCode = () => {
    setAppliedPromo(null)
    setPromoError("")
  }

  const calculatePrice = (basePrice: number) => {
    if (!appliedPromo) return basePrice
    return basePrice * (1 - appliedPromo.discountPercentage / 100)
  }

  const handleSubscribe = async (planType: PlanType) => {
    if (!session) {
      router.push("/auth/signin")
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          planType,
          promoCode: appliedPromo?.code 
        }),
      })

      if (!response.ok) {
        throw new Error("Failed to create checkout session")
      }

      const { url } = await response.json()
      window.location.href = url
    } catch (error) {
      console.error("Subscription error:", error)
      alert("Failed to start subscription process")
    } finally {
      setIsLoading(false)
    }
  }


  if (isFreeMode) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center space-y-6">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Crown className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold">Free Beta Access</h1>
            <p className="text-lg text-muted-foreground">
              BookSip is completely free for a limited time while we gather feedback. Enjoy unlimited summaries, Sipcasts, and downloads—no payment required.
            </p>
            <div className="space-y-3">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link href="/">Start Exploring</Link>
              </Button>
              <p className="text-sm text-muted-foreground">
                Subscriptions will return soon. We&apos;ll announce pricing ahead of time so you can plan.
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold mb-4">Subscription Successful!</h1>
              <p className="text-xl text-muted-foreground mb-8">
                Welcome to BookSip Premium! You now have unlimited access to all book summaries and podcasts.
              </p>
              <div className="space-y-3">
                <Button asChild size="lg" className="w-full">
                  <Link href="/">Start Reading</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="w-full">
                  <Link href="/account">View Account</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (canceled) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-8">
              <h1 className="text-3xl font-bold mb-4">Purchase Canceled</h1>
              <p className="text-xl text-muted-foreground mb-8">
                No worries! You can still enjoy your free trial or subscribe anytime.
              </p>
              <Button asChild size="lg">
                <Link href="/">Continue Browsing</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const hasActiveSubscription = userSubscription?.status === "active"

  // Show loading state while checking subscription
  if (loadingSubscription && session) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
              <p className="text-xl text-muted-foreground">
                Get unlimited access to book summaries and podcasts
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <Card key={i} className="h-[500px]">
                  <CardHeader>
                    <div className="h-6 bg-muted rounded animate-pulse mb-2" />
                    <div className="h-4 bg-muted rounded animate-pulse w-2/3" />
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="h-10 bg-muted rounded animate-pulse" />
                      <div className="space-y-2">
                        {[...Array(5)].map((_, j) => (
                          <div key={j} className="h-4 bg-muted rounded animate-pulse" />
                        ))}
                      </div>
                      <div className="h-10 bg-muted rounded animate-pulse" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-xl text-muted-foreground">
              Get unlimited access to book summaries and podcasts
            </p>
            
            {/* Plan Toggle */}
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button
                variant={selectedPlan === 'monthly' ? 'default' : 'outline'}
                onClick={() => setSelectedPlan('monthly')}
                className="px-8"
              >
                Monthly
              </Button>
              <Button
                variant={selectedPlan === 'yearly' ? 'default' : 'outline'}
                onClick={() => setSelectedPlan('yearly')}
                className="px-8"
              >
                Yearly
                <Badge className="ml-2 bg-green-600">Save {YEARLY_SAVINGS}%</Badge>
              </Button>
            </div>
          </div>

          {/* Promo Code Section */}
          {!hasActiveSubscription && (
            <div className="max-w-2xl mx-auto mb-8">
              <Card className="border-2 border-dashed border-primary/20 bg-gradient-to-r from-primary/5 to-purple-500/5">
                <CardContent className="p-6">
                  <div className="text-center space-y-4">
                    {appliedPromo && (
                      <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-4">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Sparkles className="h-5 w-5 text-green-600" />
                          <span className="font-semibold text-green-800 dark:text-green-200">
                            {appliedPromo.discountPercentage}% OFF Applied!
                          </span>
                        </div>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          {appliedPromo.description}
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-2">
                          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                            {appliedPromo.code}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={removePromoCode}
                            className="text-green-600 hover:text-green-800"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-3">
                      <div className="flex items-center justify-center gap-2">
                        <Tag className="h-5 w-5 text-primary" />
                        <h3 className="text-lg font-semibold">Have a promo code?</h3>
                      </div>
                      
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter promo code..."
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                          className="flex-1"
                          onKeyPress={(e) => e.key === 'Enter' && handlePromoCodeValidation()}
                        />
                        <Button
                          onClick={handlePromoCodeValidation}
                          disabled={isValidatingPromo || !promoCode.trim()}
                          variant="outline"
                        >
                          {isValidatingPromo ? "Validating..." : "Apply"}
                        </Button>
                      </div>

                      {promoError && (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {promoError}
                        </p>
                      )}

                      {/* Show welcome discount button when no promo is applied */}
                      {!appliedPromo && evergreenPromo && (
                        <div className="pt-2">
                          <Button
                            onClick={() => setAppliedPromo(evergreenPromo)}
                            variant="outline"
                            className="w-full border-green-200 text-green-700 hover:bg-green-50 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-950/30"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Apply Welcome Discount ({evergreenPromo.discountPercentage}% off)
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {/* Free Trial */}
            <Card className={!hasActiveSubscription ? "border-primary" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5" />
                  Free Trial
                </CardTitle>
                <CardDescription>
                  Perfect for getting started
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-4">$0</div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    5 free books
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    All formats per book
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Access to catalog
                  </li>
                </ul>
                {!hasActiveSubscription ? (
                  <Badge variant="outline" className="w-full justify-center">
                    Current Plan
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="w-full justify-center">
                    Previous Plan
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Monthly Plan */}
            <Card className={userSubscription?.planType === 'monthly' ? "border-primary ring-2 ring-primary/20" : selectedPlan === 'monthly' && !hasActiveSubscription ? "border-primary" : ""}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  Monthly
                  {userSubscription?.planType === 'monthly' && (
                    <Badge className="ml-auto bg-green-600">Active</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Flexible month-to-month
                </CardDescription>
              </CardHeader>
              <CardContent>
                {appliedPromo ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-muted-foreground line-through">${MONTHLY_PRICE}</span>
                      <Badge variant="destructive" className="text-xs">
                        -{appliedPromo.discountPercentage}%
                      </Badge>
                    </div>
                    <div className="text-3xl font-bold text-green-600">
                      ${calculatePrice(MONTHLY_PRICE).toFixed(2)}
                      <span className="text-lg text-muted-foreground">/month</span>
                    </div>
                    <p className="text-sm text-green-600 font-medium">
                      You save ${(MONTHLY_PRICE * (appliedPromo.discountPercentage / 100)).toFixed(2)}/month!
                    </p>
                  </div>
                ) : (
                  <div className="text-3xl font-bold mb-4">
                    ${MONTHLY_PRICE}
                    <span className="text-lg text-muted-foreground">/month</span>
                  </div>
                )}
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Unlimited book summaries
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Unlimited podcasts
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    All summary formats
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Download offline
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Cancel anytime
                  </li>
                </ul>
                {userSubscription?.planType === 'monthly' ? (
                  <Badge variant="outline" className="w-full justify-center bg-green-50 text-green-700 border-green-200">
                    ✅ Active Plan
                  </Badge>
                ) : hasActiveSubscription ? (
                  <Button 
                    disabled
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Already Subscribed
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleSubscribe('monthly')}
                    disabled={isLoading}
                    variant={selectedPlan === 'monthly' ? 'default' : 'outline'}
                    className="w-full"
                    size="lg"
                  >
                    {isLoading && selectedPlan === 'monthly' ? "Processing..." : "Subscribe Monthly"}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Yearly Plan */}
            <Card className={userSubscription?.planType === 'yearly' ? "border-primary ring-2 ring-primary/20" : selectedPlan === 'yearly' && !hasActiveSubscription ? "border-primary ring-2 ring-primary/20" : ""}>
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-gradient-to-r from-green-600 to-emerald-600">
                    Best Value - Save {YEARLY_SAVINGS}%
                  </Badge>
                </div>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  Yearly
                  {userSubscription?.planType === 'yearly' && (
                    <Badge className="ml-auto bg-green-600">Active</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Best value for serious learners
                </CardDescription>
              </CardHeader>
              <CardContent>
                {appliedPromo ? (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-muted-foreground line-through">${YEARLY_PRICE}</span>
                      <Badge variant="destructive" className="text-xs">
                        -{appliedPromo.discountPercentage}%
                      </Badge>
                    </div>
                    <div className="text-3xl font-bold text-green-600">
                      ${calculatePrice(YEARLY_PRICE).toFixed(2)}
                      <span className="text-lg text-muted-foreground">/year</span>
                    </div>
                    <p className="text-sm text-green-600 font-medium">
                      You save ${(YEARLY_PRICE * (appliedPromo.discountPercentage / 100)).toFixed(2)}!
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Only ${(calculatePrice(YEARLY_PRICE) / 12).toFixed(2)}/month
                    </p>
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="text-3xl font-bold">
                      ${YEARLY_PRICE}
                      <span className="text-lg text-muted-foreground">/year</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Only ${YEARLY_MONTHLY_EQUIVALENT}/month
                    </p>
                  </div>
                )}
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Unlimited book summaries
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Unlimited podcasts
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    All summary formats
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Download offline
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    Cancel anytime
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="font-semibold text-green-600">2 months free!</span>
                  </li>
                </ul>
                {userSubscription?.planType === 'yearly' ? (
                  <Badge variant="outline" className="w-full justify-center bg-green-50 text-green-700 border-green-200">
                    ✅ Active Plan
                  </Badge>
                ) : hasActiveSubscription ? (
                  <Button 
                    disabled
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Already Subscribed
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleSubscribe('yearly')}
                    disabled={isLoading}
                    variant={selectedPlan === 'yearly' ? 'default' : 'outline'}
                    className="w-full"
                    size="lg"
                  >
                    {isLoading && selectedPlan === 'yearly' ? "Processing..." : "Subscribe Yearly"}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Features */}
          <div className="text-center mt-12">
            <h2 className="text-2xl font-bold mb-8">Why Choose BookSip?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Zap className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Comprehensive Summaries</h3>
                <p className="text-muted-foreground">
                  Get comprehensive, accurate summaries in multiple formats
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Crown className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Engaging Podcasts</h3>
                <p className="text-muted-foreground">
                  Listen to engaging podcasts that bring books to life
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Check className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">Cancel Anytime</h3>
                <p className="text-muted-foreground">
                  No long-term commitment - cancel your subscription whenever you want
                </p>
              </div>
            </div>
          </div>

          {/* FAQ Note */}
          <div className="mt-12 p-6 bg-muted/50 rounded-lg">
            <h3 className="font-semibold mb-3 text-center">Important Information</h3>
            <ul className="space-y-2 text-sm text-muted-foreground max-w-2xl mx-auto">
              <li className="flex gap-2">
                <span>•</span>
                <span>Your subscription will automatically renew each {selectedPlan === 'monthly' ? 'month' : 'year'} unless canceled</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>You can cancel anytime from your account settings with no penalties</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>If you cancel, you&apos;ll retain access until the end of your billing period</span>
              </li>
              <li className="flex gap-2">
                <span>•</span>
                <span>Promo codes apply to your first payment only</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function SubscriptionPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SubscriptionContent />
    </Suspense>
  )
}
